import React from "react";
import { Link } from "react-router-dom";
import {
  Eye,
  Pencil,
  Power,
  PowerOff,
  ShieldCheck,
  ShieldOff,
  ShieldAlert,
  Trash2,
  UserRoundMinus,
  UserRoundPlus,
} from "lucide-react";
import { FaChevronDown, FaChevronUp, FaInbox } from "react-icons/fa";
import {
  ACTION_ICON_BTN,
  ACTION_VARIANTS,
  BADGE_BASE,
  getIpCheckBadgeClass,
  getRoleBadgeClass,
  getStatusBadgeClass,
  TABLE_CELL_CLASS,
  TABLE_HEAD_CLASS,
} from "./accountsConstants";

function SortIcon({ active, direction }) {
  if (!active) {
    return (
      <span className="ml-1 inline-flex flex-col text-gray-300">
        <FaChevronUp size={7} />
        <FaChevronDown size={7} className="-mt-0.5" />
      </span>
    );
  }
  return direction === "ASC" ? (
    <FaChevronUp size={8} className="ml-1 text-[#007BFF]" />
  ) : (
    <FaChevronDown size={8} className="ml-1 text-[#007BFF]" />
  );
}

function AccountIconButton({
  title,
  onClick,
  disabled,
  variant = "slate",
  to,
  children,
}) {
  const className = `${ACTION_ICON_BTN} ${ACTION_VARIANTS[variant] || ACTION_VARIANTS.slate}`;

  if (to) {
    return (
      <Link to={to} className={className} title={title} aria-label={title}>
        {children}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      style={{ boxShadow: "none" }}
    >
      {children}
    </button>
  );
}

function StatusBadge({ active, label }) {
  return (
    <span className={`${BADGE_BASE} ${getStatusBadgeClass(active)}`}>
      {label}
    </span>
  );
}

function IpCheckBadge({ value }) {
  return (
    <span className={`${BADGE_BASE} ${getIpCheckBadgeClass(value)}`}>
      {value || "—"}
    </span>
  );
}

function RoleBadge({ role }) {
  return (
    <span className={`${BADGE_BASE} ${getRoleBadgeClass(role)}`}>
      {role || "—"}
    </span>
  );
}

function AccountsTable({
  users,
  headers,
  sortColumn,
  sortOrder,
  onSort,
  rowStartIndex,
  isTeamLeadUser,
  isActiveUser,
  isHrAdminUser,
  savingTeamLeadId,
  savingStatusId,
  savingHrAdminId,
  saving2faResetId,
  canManageTwoFactor,
  currentUserId,
  onResetTwoFactor,
  selectedUserIds = [],
  onToggleUser,
  onToggleAll,
  onToggleTeamLead,
  onToggleStatus,
  onToggleHrAdmin,
  onDeleteUser,
  onViewAccount,
  onEditAccount,
}) {
  if (!users?.length) {
    return (
      <div className="px-3 py-12 text-center">
        <div className="mx-auto flex max-w-sm flex-col items-center">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100">
            <FaInbox size={16} className="text-gray-400" />
          </div>
          <p className="text-sm font-semibold text-gray-900">No accounts found</p>
          <p className="mt-0.5 text-xs text-gray-500">
            Try adjusting your search filters
          </p>
        </div>
      </div>
    );
  }

  let count = rowStartIndex;
  const selectedSet = new Set((selectedUserIds || []).map((id) => String(id)));
  const allSelected =
    users.length > 0 && users.every((user) => selectedSet.has(String(user.id)));
  const someSelected = users.some((user) => selectedSet.has(String(user.id)));

  return (
    <div className="overflow-x-auto">
      <table id="viewaccounts" className="w-full min-w-[960px] text-left text-xs">
        <thead className={TABLE_HEAD_CLASS}>
          <tr>
            <th className={`${TABLE_CELL_CLASS} w-10 text-center`}>
              <input
                type="checkbox"
                className="accounts-row-checkbox"
                checked={allSelected}
                ref={(input) => {
                  if (input) input.indeterminate = someSelected && !allSelected;
                }}
                onChange={(e) => onToggleAll?.(e.target.checked)}
                aria-label="Select all accounts on this page"
              />
            </th>
            {headers.map((head) => {
              const sortable = head.sortable !== false && head.dataKey && head.dataKey !== "actions";
              const isActive = sortable && sortColumn === head.dataKey;
              return (
                <th
                  key={head.key}
                  id={head.dataKey}
                  className={`whitespace-nowrap ${TABLE_CELL_CLASS} ${
                    head.align === "text-center"
                      ? "text-center"
                      : head.dataKey === "actions"
                        ? "text-right"
                        : "text-left"
                  } ${sortable ? "cursor-pointer select-none" : ""}`}
                  onClick={sortable ? () => onSort(head.dataKey) : undefined}
                >
                  <span className="inline-flex items-center">
                    {head.title}
                    {sortable && (
                      <SortIcon active={isActive} direction={sortOrder} />
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {users.map((value) => {
            const teamLead = isTeamLeadUser(value);
            const isSavingTeamLead = savingTeamLeadId === value.id;
            const isSavingStatus = savingStatusId === value.id;
            const isSavingHrAdmin = savingHrAdminId === value.id;
            const isActive = isActiveUser(value);
            const isHrAdmin = isHrAdminUser(value);
            const isSaving2faReset = saving2faResetId === value.id;
            const isRowBusy =
              isSavingTeamLead || isSavingStatus || isSavingHrAdmin || isSaving2faReset;
            const showReset2fa =
              canManageTwoFactor &&
              value.two_factor_reset_allowed &&
              String(value.id) !== String(currentUserId);

            return (
              <tr
                key={value.id}
                className={`transition hover:bg-[#F8FAFC] ${
                  selectedSet.has(String(value.id)) ? "bg-[#007BFF]/[0.03]" : ""
                }`}
              >
                <td className={`${TABLE_CELL_CLASS} text-center`}>
                  <input
                    type="checkbox"
                    className="accounts-row-checkbox"
                    checked={selectedSet.has(String(value.id))}
                    onChange={(e) => onToggleUser?.(value.id, e.target.checked)}
                    aria-label={`Select ${value.name}`}
                  />
                </td>
                <td className={`${TABLE_CELL_CLASS} text-center tabular-nums text-gray-500`}>
                  {count++}
                </td>
                <td className={`${TABLE_CELL_CLASS} font-medium text-[#35314d]`}>
                  {value.name}
                </td>
                <td className={`${TABLE_CELL_CLASS} text-center`}>
                  <RoleBadge role={value.role} />
                </td>
                <td className={`${TABLE_CELL_CLASS} text-center text-gray-600`}>
                  {value.branchname || "—"}
                </td>
                <td className={`${TABLE_CELL_CLASS} text-center`}>
                  <StatusBadge active={isActive} label={value.activestatus} />
                </td>
                <td className={`${TABLE_CELL_CLASS} text-center`}>
                  <IpCheckBadge value={value.ip_check} />
                </td>
                <td className={`${TABLE_CELL_CLASS} text-center tabular-nums text-gray-600`}>
                  {value.mobile || "—"}
                </td>
                <td className={`${TABLE_CELL_CLASS} text-center`}>
                  <div className="flex items-center justify-center gap-1.5">
                    <AccountIconButton
                      title={teamLead ? "Remove Team Lead" : "Make Team Lead"}
                      variant={teamLead ? "red" : "blue"}
                      onClick={() => onToggleTeamLead(value)}
                      disabled={isRowBusy}
                    >
                      {teamLead ? (
                        <UserRoundMinus size={14} strokeWidth={2.2} />
                      ) : (
                        <UserRoundPlus size={14} strokeWidth={2.2} />
                      )}
                    </AccountIconButton>
                  </div>
                </td>
                <td className={`${TABLE_CELL_CLASS} text-center`}>
                  <div className="flex items-center justify-center gap-1.5">
                    <AccountIconButton
                      title={isHrAdmin ? "Remove HR Admin" : "Make HR Admin"}
                      variant={isHrAdmin ? "red" : "blue"}
                      onClick={() => onToggleHrAdmin(value)}
                      disabled={isRowBusy}
                    >
                      {isHrAdmin ? (
                        <ShieldOff size={14} strokeWidth={2.2} />
                      ) : (
                        <ShieldCheck size={14} strokeWidth={2.2} />
                      )}
                    </AccountIconButton>
                  </div>
                </td>
                <td className={`${TABLE_CELL_CLASS} text-right`}>
                  <div className="flex items-center justify-end gap-1.5">
                    <AccountIconButton
                      title="View Detail"
                      variant="slate"
                      onClick={() => onViewAccount(value.id)}
                    >
                      <Eye size={14} strokeWidth={2.2} />
                    </AccountIconButton>

                    <AccountIconButton
                      title="Edit Account"
                      variant="blue"
                      onClick={() => onEditAccount(value.id)}
                    >
                      <Pencil size={14} strokeWidth={2.2} />
                    </AccountIconButton>

                    {showReset2fa && (
                      <AccountIconButton
                        title="Reset 2FA (lockout recovery)"
                        variant="amber"
                        onClick={() => onResetTwoFactor?.(value)}
                        disabled={isRowBusy}
                      >
                        <ShieldAlert size={14} strokeWidth={2.2} />
                      </AccountIconButton>
                    )}

                    <AccountIconButton
                      title="Delete Account"
                      variant="red"
                      onClick={() => onDeleteUser(value.id, value.name)}
                      disabled={isRowBusy}
                    >
                      <Trash2 size={14} strokeWidth={2.2} />
                    </AccountIconButton>

                    <AccountIconButton
                      title={isActive ? "Deactivate" : "Activate"}
                      variant={isActive ? "red" : "green"}
                      onClick={() => onToggleStatus(value)}
                      disabled={isRowBusy}
                    >
                      {isActive ? (
                        <PowerOff size={14} strokeWidth={2.2} />
                      ) : (
                        <Power size={14} strokeWidth={2.2} />
                      )}
                    </AccountIconButton>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AccountsTable;
